import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Client, Company } from 'src/constants';
import { ActionButtonModal } from 'src/legacy/components/Modals';
import { useManageCompanyClientsMutation } from 'src/services/api/companiesApi';

type PlaceholderData = {
  company: Company;
  client: Client;
} | null;

export type AssignClientToCompanyParams = {
  company: Company;
  client: Client;
  promotePlaceholders?: boolean;
  deletePlaceholder?: boolean;
};

export type UseCompanyClientAssignmentReturn = {
  /**
   * Assigns a client to a company. Resolves to `true` if the client was
   * assigned immediately, or `false` if a placeholder conflict requires
   * user confirmation via one of the modals below (or the request failed).
   */
  assignClientToCompany: (
    params: AssignClientToCompanyParams,
  ) => Promise<boolean>;
  PlaceholderPromotionModal: React.FC;
  PlaceholderDeletionModal: React.FC;
  isLoading: boolean;
};

/**
 * Custom hook for managing company-client assignments with placeholder handling.
 *
 * This hook encapsulates the logic for:
 * - Assigning clients to companies
 * - Handling placeholder promotion conflicts (creating new personal companies)
 * - Handling placeholder deletion conflicts (moving data between companies)
 * - Managing modal states for user confirmation
 *
 * @returns {UseCompanyClientAssignmentReturn} Object containing:
 *   - assignClientToCompany: Function to assign a client to a company
 *   - PlaceholderPromotionModal: Modal for confirming placeholder company promotion
 *   - PlaceholderDeletionModal: Modal for confirming placeholder company deletion
 *   - isLoading: Boolean indicating if assignment is in progress
 *
 * @example
 * ```tsx
 * const {
 *   assignClientToCompany,
 *   PlaceholderPromotionModal,
 *   PlaceholderDeletionModal
 * } = useCompanyClientAssignment();
 *
 * const handleAddClient = async () => {
 *   await assignClientToCompany({
 *     company: company,
 *     client: client,
 *   });
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleAddClient}>Add Client</button>
 *     <PlaceholderPromotionModal />
 *     <PlaceholderDeletionModal />
 *   </>
 * );
 * ```
 */
export const useCompanyClientAssignment =
  (): UseCompanyClientAssignmentReturn => {
    const { t } = useTranslation();
    const [manageCompanyClients, { isLoading }] =
      useManageCompanyClientsMutation();

    const [placeholderPromotionData, setPlaceholderPromotionData] =
      React.useState<PlaceholderData>(null);
    const [placeholderDeletionData, setPlaceholderDeletionData] =
      React.useState<PlaceholderData>(null);

    const handleOpenPlaceholderPromotionModal = (
      company: Company,
      client: Client,
    ) => {
      setPlaceholderPromotionData({ company, client });
    };

    const handleClosePlaceholderPromotionModal = () => {
      setPlaceholderPromotionData(null);
    };

    const handleOpenPlaceholderDeletionModal = (
      company: Company,
      client: Client,
    ) => {
      setPlaceholderDeletionData({ company, client });
    };

    const handleClosePlaceholderDeletionModal = () => {
      setPlaceholderDeletionData(null);
    };

    const assignClientToCompany = React.useCallback(
      async ({
        company,
        client,
        promotePlaceholders = false,
        deletePlaceholder = false,
      }: AssignClientToCompanyParams) => {
        const data = await manageCompanyClients({
          companyId: company.id,
          addClientIds: [client.id],
          removeClientIds: [],
          promotePlaceholders,
          deletePlaceholder,
        }).unwrap();

        const placeholderFailedIds = data.addClientErrors.filter(
          (error) =>
            error.error.type === 'record_conflict' ||
            error.error.type === 'require_confirmation',
        );

        if (placeholderFailedIds.length > 0) {
          const isRetry = placeholderFailedIds.some(
            (error) => error.error.type === 'require_confirmation',
          );

          if (isRetry) {
            handleOpenPlaceholderDeletionModal(company, client);
          } else {
            handleOpenPlaceholderPromotionModal(company, client);
          }
          return false;
        }

        return data.addClientErrors.length === 0;
      },
      [manageCompanyClients],
    );

    const handlePromotePlaceholder = async () => {
      if (placeholderPromotionData) {
        await assignClientToCompany({
          company: placeholderPromotionData.company,
          client: placeholderPromotionData.client,
          promotePlaceholders: true,
        });
        handleClosePlaceholderPromotionModal();
      }
    };

    const handleDeletePlaceholder = async () => {
      if (placeholderDeletionData) {
        await assignClientToCompany({
          company: placeholderDeletionData.company,
          client: placeholderDeletionData.client,
          deletePlaceholder: true,
        });
        handleClosePlaceholderDeletionModal();
      }
    };

    const PlaceholderPromotionModal: React.FC = () =>
      placeholderPromotionData ? (
        <ActionButtonModal
          open={placeholderPromotionData !== null}
          onClose={handleClosePlaceholderPromotionModal}
          positiveAction
          onPositiveActionPerformed={handlePromotePlaceholder}
          title={t(($) => $.page_clients.modals.placeholder_promotion.title)}
          positiveActionText={t(($) => $.common.confirm)}
          mainComponent={
            <Trans
              i18nKey={'page_clients.modals.placeholder_promotion.description'}
              values={{
                newCompanyName: `${placeholderPromotionData?.client.fields?.givenName} ${placeholderPromotionData?.client.fields?.familyName} (Personal)`,
                oldCompanyName: placeholderPromotionData?.company.fields.name,
              }}
              components={{ P: <p /> }}
            />
          }
        />
      ) : null;

    const PlaceholderDeletionModal: React.FC = () =>
      placeholderDeletionData ? (
        <ActionButtonModal
          open={placeholderDeletionData !== null}
          onClose={handleClosePlaceholderDeletionModal}
          positiveAction
          onPositiveActionPerformed={handleDeletePlaceholder}
          title={t(($) => $.page_clients.modals.placeholder_deletion.title)}
          positiveActionText={t(($) => $.common.confirm)}
          mainComponent={
            <Trans
              i18nKey={'page_clients.modals.placeholder_deletion.description'}
              values={{
                newCompanyName: placeholderDeletionData?.company.fields.name,
              }}
              components={{ P: <p /> }}
            />
          }
        />
      ) : null;

    return {
      assignClientToCompany,
      PlaceholderPromotionModal,
      PlaceholderDeletionModal,
      isLoading,
    };
  };
